import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

export default function AdminFeedback() {
  const { user } = useAuth();
  const { l } = useLanguage();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [stats, setStats] = useState(null);

  const getTypeLabel = (type) => {
    const labels = {
      suggestion: l('Suggestion', 'အကြံပြုချက်', '建议'),
      bug_report: l('Bug Report', 'ချို့ယွင်းချက်', '错误报告'),
      feature_request: l('Feature Request', 'အင်္ဂါရပ် တောင်းဆိုချက်', '功能请求'),
      general: l('General', 'ယေဘုယျ', '一般'),
      other: l('Other', 'အခြား', '其他')
    };
    return labels[type] || type;
  };

  useEffect(() => {
    if (!user?.is_admin) {
      setLoading(false);
      return;
    }

    fetchFeedbacks();
    fetchStats();
  }, [user, statusFilter, typeFilter]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await api.getAllFeedback(
        statusFilter !== 'all' ? statusFilter : null, 
        typeFilter !== 'all' ? typeFilter : null
      );
      
      if (Array.isArray(data)) {
        setFeedbacks(data);
      } else {
        setFeedbacks([]);
        console.warn('Unexpected response format:', data);
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
      const errorMessage = err.message || 'Unable to load feedback';
      setError(`${errorMessage}. Please check your connection and ensure you have admin access.`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await api.getFeedbackStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
      // Don't show error for stats, it's not critical
    }
  };

  const updateStatus = async (feedbackId, newStatus, adminNotes = '') => {
    try {
      setUpdating(true);
      await api.updateFeedbackStatus(feedbackId, newStatus, adminNotes);
      await fetchFeedbacks();
      await fetchStats();
      setSelectedFeedback(null);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      reviewed: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      in_progress: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      resolved: 'bg-green-500/10 text-green-500 border-green-500/20',
      closed: 'bg-gray-500/10 text-gray-500 border-gray-500/20'
    };
    return colors[status] || colors.new;
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">
            {l('Access Denied', 'ဝင်ရောက်ခွင့်မရှိပါ', '访问被拒绝')}
          </h1>
          <p className="text-muted-foreground">
            {l('Admin access required', 'Admin ဝင်ရောက်ခွင့် လိုအပ်ပါသည်', '需要管理员权限')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            {l('Feedback Management', 'တုံ့ပြန်ချက် စီမံခန့်ခွဲမှု', '反馈管理')}
          </h1>
          <p className="text-muted-foreground">
            {l(
              'View and manage all user feedback',
              'အသုံးပြုသူ အားလုံး၏ တုံ့ပြန်ချက်များကို ကြည့်ရှုနှင့် စီမံခန့်ခွဲရန်',
              '查看和管理所有用户反馈'
            )}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-foreground">{stats.total}</div>
              <div className="text-sm text-muted-foreground">{l('Total', 'စုစုပေါင်း', '总计')}</div>
            </div>
            {Object.entries(stats.by_status).map(([status, count]) => (
              <div key={status} className="bg-card border border-border rounded-lg p-4">
                <div className="text-2xl font-bold text-foreground">{count}</div>
                <div className="text-sm text-muted-foreground capitalize">{status.replace('_', ' ')}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {l('Filter by Status', 'အခြေအနေ စစ်ထုတ်ရန်', '按状态筛选')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">{l('All Status', 'အခြေအနေ အားလုံး', '全部状态')}</option>
                <option value="new">{l('New', 'အသစ်', '新建')}</option>
                <option value="reviewed">{l('Reviewed', 'ပြန်လည် စစ်ဆေးပြီး', '已审阅')}</option>
                <option value="in_progress">{l('In Progress', 'လုပ်ဆောင်နေသည်', '进行中')}</option>
                <option value="resolved">{l('Resolved', 'ဖြေရှင်းပြီး', '已解决')}</option>
                <option value="closed">{l('Closed', 'ပိတ်ထားသည်', '已关闭')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {l('Filter by Type', 'အမျိုးအစား စစ်ထုတ်ရန်', '按类型筛选')}
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">{l('All Types', 'အမျိုးအစား အားလုံး', '全部类型')}</option>
                <option value="suggestion">{l('Suggestion', 'အကြံပြုချက်', '建议')}</option>
                <option value="bug_report">{l('Bug Report', 'ချို့ယွင်းချက်', '错误报告')}</option>
                <option value="feature_request">{l('Feature Request', 'အင်္ဂါရပ် တောင်းဆိုချက်', '功能请求')}</option>
                <option value="general">{l('General', 'ယေဘုယျ', '一般')}</option>
                <option value="other">{l('Other', 'အခြား', '其他')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Feedback List */}
        <div className="space-y-4">
          {feedbacks.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <p className="text-muted-foreground">
                {l('No feedback found', 'တုံ့ပြန်ချက် မတွေ့ရှိပါ', '未找到反馈')}
              </p>
            </div>
          ) : (
            feedbacks.map((feedback) => (
              <div
                key={feedback.id}
                className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all cursor-pointer"
                onClick={() => setSelectedFeedback(feedback)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(feedback.status)}`}>
                        {feedback.status.replace('_', ' ')}
                      </span>
                      <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                        {getTypeLabel(feedback.feedback_type)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{feedback.subject}</h3>
                    <p className="text-muted-foreground line-clamp-2 mb-3">{feedback.message}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {feedback.contact_name && (
                        <span>{l('From', 'မှ', '来自')}: {feedback.contact_name}</span>
                      )}
                      {feedback.contact_email && (
                        <span>{feedback.contact_email}</span>
                      )}
                      <span>
                        {new Date(feedback.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Feedback Detail Modal */}
        {selectedFeedback && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedFeedback(null)}>
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedFeedback.status)}`}>
                      {selectedFeedback.status.replace('_', ' ')}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                      {getTypeLabel(selectedFeedback.feedback_type)}
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedFeedback(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">{selectedFeedback.subject}</h2>
                <div className="text-sm text-muted-foreground">
                  {selectedFeedback.contact_name && <span>{l('From', 'မှ', '来自')}: {selectedFeedback.contact_name} </span>}
                  {selectedFeedback.contact_email && <span>({selectedFeedback.contact_email}) </span>}
                  <span>• {new Date(selectedFeedback.created_at).toLocaleString()}</span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground mb-2">{l('Message', 'မက်ဆေ့ချ်', '消息')}</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">{selectedFeedback.message}</p>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-sm font-semibold text-foreground mb-4">{l('Update Status', 'အခြေအနေ အပ်ဒိတ်လုပ်ရန်', '更新状态')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {['new', 'reviewed', 'in_progress', 'resolved', 'closed'].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateStatus(selectedFeedback.id, status)}
                        disabled={updating || selectedFeedback.status === status}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                          selectedFeedback.status === status
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        } disabled:opacity-50`}
                      >
                        {status.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
